import React, { useMemo } from 'react';
import { Typography } from 'antd';
import { Line } from '@ant-design/plots';

const { Text } = Typography;

export interface LineMetricPoint {
  x?: string;
  bucket?: string;
  average?: number | null;
  avg?: number | null;
  min?: number | null;
  max?: number | null;
  sum?: number | null;
  count?: number | null;
  median?: number | null;
  variance?: number | null;
  stddev?: number | null;
  unit?: string | null;
  patternPointId?: number | null;
}

export interface LineMetricSeries {
  metricKey: string;
  aggregations?: string[];
  points?: LineMetricPoint[];
}

interface Props {
  title?: string;
  data?: LineMetricSeries[];
  fields?: string[];
  metricLabels?: Record<string, string>;
  metrics?: any[];  // All metrics for ID lookup
  patternPoints?: any[];  // Pattern points for labeling
  showParent?: boolean;
  showPoints?: boolean;
  previewFallback?: boolean;
  fallbackMetricKey?: string;
  previewAreaId?: number;  // Area context for metric links
  onPatternPointClick?: (patternPointId: number, patternId: number, areaId: number) => void;
  onMetricClick?: (metricKey: string, metricId: number | undefined, areaId: number) => void;
}

const FIELD_LABELS: Record<string, string> = {
  average: 'Average',
  min: 'Min',
  max: 'Max',
  count: 'Count',
  sum: 'Sum',
  median: 'Median',
  variance: 'Variance',
  stddev: 'Std Dev',
};

const DEFAULT_PALETTE = ['#1d4ed8', '#0f766e', '#16a34a', '#f59e0b', '#ef4444', '#7c3aed'];

const toRgba = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace('#', '').trim();
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgba(22, 119, 255, ${alpha})`;
};

const normalizeField = (field: string) => (field === 'avg' ? 'average' : field);

const fieldOrder = ['average', 'min', 'max', 'count', 'sum', 'median', 'variance', 'stddev'] as const;
const sortFields = (fields: string[]) => {
  const unique = Array.from(new Set(fields));
  const ordered = fieldOrder.filter((f) => unique.includes(f));
  const rest = unique.filter((f) => !fieldOrder.includes(f as any));
  return [...ordered, ...rest];
};

const formatAggregationLabel = (field: string) => {
  const normalized = normalizeField(field);
  return FIELD_LABELS[normalized] || normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

type XScaleType = 'time' | 'linear' | 'cat';

function parseXCandidate(input: unknown): { kind: 'time'; ms: number } | { kind: 'linear'; n: number } | { kind: 'cat'; s: string } | { kind: 'null' } {
  if (input === null || input === undefined) return { kind: 'null' };

  if (input instanceof Date) {
    const ms = input.getTime();
    return Number.isFinite(ms) ? { kind: 'time', ms } : { kind: 'null' };
  }

  if (typeof input === 'number') {
    return Number.isFinite(input) ? { kind: 'linear', n: input } : { kind: 'null' };
  }

  // strings (or anything else)
  const s = String(input).trim();
  if (!s) return { kind: 'null' };

  // try time first
  const ms = Date.parse(s);
  if (!Number.isNaN(ms)) return { kind: 'time', ms };

  // then number
  const n = Number(s);
  if (Number.isFinite(n)) return { kind: 'linear', n };

  // fallback categorical
  return { kind: 'cat', s };
}

function chooseXScaleType(candidates: ReturnType<typeof parseXCandidate>[]): XScaleType {
  const nonNull = candidates.filter((c) => c.kind !== 'null');
  if (nonNull.length === 0) return 'time'; // default; chart will show "No data" anyway

  const allTime = nonNull.every((c) => c.kind === 'time');
  if (allTime) return 'time';

  const allLinear = nonNull.every((c) => c.kind === 'linear');
  if (allLinear) return 'linear';

  // mixed or categorical -> safest is category
  return 'cat';
}

function coerceXToScale(input: unknown, scaleType: XScaleType): { x: Date | number | string; sortKey: number | string } | null {
  const parsed = parseXCandidate(input);
  if (parsed.kind === 'null') return null;

  if (scaleType === 'time') {
    // if not time, coerce to string -> date parse may be wrong; better to drop rather than lie
    if (parsed.kind !== 'time') return null;
    const d = new Date(parsed.ms);
    return { x: d, sortKey: parsed.ms };
  }

  if (scaleType === 'linear') {
    if (parsed.kind !== 'linear') return null;
    return { x: parsed.n, sortKey: parsed.n };
  }

  // cat
  if (parsed.kind === 'time') {
    // stable category string for dates
    const iso = new Date(parsed.ms).toISOString();
    return { x: iso, sortKey: iso };
  }
  if (parsed.kind === 'linear') {
    const s = String(parsed.n);
    return { x: s, sortKey: s };
  }
  return { x: parsed.s, sortKey: parsed.s };
}

type PlotPoint = { x: Date | number | string; value: number; series: string; __seriesKey: string; __sortKey: number | string; __isParent?: boolean };
type PlotBand = { x: Date | number | string; min: number; max: number; bandSeries?: string; __sortKey: number | string };

export const ReportPatternPointLineChart: React.FC<Props> = ({
  title,
  data,
  fields,
  metricLabels,
  metrics,
  patternPoints,
  showParent = true,
  showPoints = false,
  previewFallback,
  fallbackMetricKey,
  previewAreaId,
  onPatternPointClick,
  onMetricClick,
}) => {
  const { accentColor, palette } = useMemo(() => {
    const lineData = Array.isArray(data) ? data : [];
    const primaryMetricKey = lineData[0]?.metricKey || fallbackMetricKey;
    const metric = Array.isArray(metrics)
      ? metrics.find((entry) => entry?.key === primaryMetricKey)
      : undefined;
    const metricPalette = Array.isArray(metric?.colorPalette) ? metric.colorPalette.filter(Boolean) : [];
    const resolvedPalette = metricPalette.length > 0 ? metricPalette : DEFAULT_PALETTE;
    return {
      accentColor: resolvedPalette[0],
      palette: resolvedPalette,
    };
  }, [data, metrics, fallbackMetricKey]);

  const {
    displayPoints,
    displayBands,
    unitLabel,
    xScaleType,
    showPlaceholder,
  } = useMemo(() => {
    const lineData = Array.isArray(data) ? data : [];
    const selectedFields = Array.isArray(fields) ? fields.map((f) => normalizeField(f)) : [];
    const showMetricLabel = lineData.length > 1;

    // DEBUG: Log raw backend data
    console.group('🔍 Pattern Point Line Chart - Debug');
    console.log('📊 Raw Backend Data:', JSON.stringify(lineData, null, 2));
    console.log('📍 Pattern Points:', patternPoints);
    console.log('⚙️ Config:', { showParent, showPoints, selectedFields });

    // collect units
    const unitValues = new Set<string>();
    lineData.forEach((metricEntry) => {
      (metricEntry.points ?? []).forEach((p) => {
        if (p.unit) unitValues.add(p.unit);
      });
    });
    const resolvedUnitLabel = unitValues.size === 1 ? Array.from(unitValues)[0] : undefined;

    // first pass: gather x candidates to decide scale type
    const xCandidates: ReturnType<typeof parseXCandidate>[] = [];
    lineData.forEach((metricEntry) => {
      (metricEntry.points ?? []).forEach((p) => {
        const rawX = p.x ?? p.bucket;
        xCandidates.push(parseXCandidate(rawX));
      });
    });

    const resolvedXScaleType = chooseXScaleType(xCandidates);

    const points: PlotPoint[] = [];
    const bands: PlotBand[] = [];

    lineData.forEach((metricEntry) => {
      const metricKey = metricEntry.metricKey || fallbackMetricKey || 'metric';
      const metricLabel = metricLabels?.[metricKey] || metricKey;

      const pointList = Array.isArray(metricEntry.points) ? metricEntry.points : [];

      // determine available fields
      const availableFields = new Set<string>();
      if (Array.isArray(metricEntry.aggregations) && metricEntry.aggregations.length > 0) {
        metricEntry.aggregations.forEach((agg) => availableFields.add(normalizeField(agg)));
      } else if (pointList.length > 0) {
        ['average', 'avg', 'min', 'max', 'count', 'sum', 'median', 'variance', 'stddev'].forEach((field) => {
          if (
            pointList.some(
              (p) =>
                (p as any)[field] !== undefined &&
                (p as any)[field] !== null
            )
          ) {
            availableFields.add(normalizeField(field));
          }
        });
      }

      const resolvedFields =
        selectedFields.length > 0
          ? availableFields.size > 0
            ? selectedFields.filter((f) => availableFields.has(f))
            : selectedFields
          : Array.from(availableFields);

      const fieldsToUse = sortFields(resolvedFields.length > 0 ? resolvedFields : ['average']);
      const hasBandFields = ['average', 'min', 'max'].every((f) => fieldsToUse.includes(f));

      // Group points by date and optionally by patternPointId based on showParent/showPoints
      type AggregatedPoint = {
        x: any;
        patternPointId?: number;
        unit?: string | null;
        values: { [field: string]: number[] };
        isParent?: boolean;
      };

      const aggregationMap = new Map<string, AggregatedPoint>();

      // DEBUG: Log data points for this metric
      console.log(`\n📈 Processing Metric: ${metricKey}`);
      console.log(`  Total points from backend: ${pointList.length}`);
      if (pointList.length > 0) {
        console.log(`  Sample point:`, pointList[0]);
      }

      // Process points for parent aggregation (if enabled)
      if (showParent) {
        pointList.forEach((p: any) => {
          const rawX = p.x ?? p.bucket;
          const xKey = String(rawX || '');
          const mapKey = `parent-${xKey}`;

          if (!aggregationMap.has(mapKey)) {
            aggregationMap.set(mapKey, {
              x: rawX,
              patternPointId: undefined,
              unit: p.unit,
              values: {},
              isParent: true,
            });
          }

          const entry = aggregationMap.get(mapKey)!;

          // Collect all numeric field values for aggregation
          fieldsToUse.forEach((field) => {
            const rawValue = field === 'average' ? p.average ?? p.avg : (p as any)[field];
            if (rawValue !== null && rawValue !== undefined) {
              if (!entry.values[field]) entry.values[field] = [];
              const num = typeof rawValue === 'number' ? rawValue : Number(rawValue);
              if (Number.isFinite(num)) {
                entry.values[field].push(num);
              }
            }
          });
        });
      }

      // Process points for pattern points breakdown (if enabled)
      if (showPoints) {
        pointList.forEach((p: any) => {
          const rawX = p.x ?? p.bucket;
          const xKey = String(rawX || '');
          const patternPointId = p.patternPointId;

          if (patternPointId === null || patternPointId === undefined) return;

          const mapKey = `point-${xKey}-pp${patternPointId}`;

          if (!aggregationMap.has(mapKey)) {
            aggregationMap.set(mapKey, {
              x: rawX,
              patternPointId,
              unit: p.unit,
              values: {},
              isParent: false,
            });
          }

          const entry = aggregationMap.get(mapKey)!;

          // For pattern points, use the raw values directly (no aggregation)
          // Backend already provides one value per pattern point per day
          fieldsToUse.forEach((field) => {
            const rawValue = field === 'average' ? p.average ?? p.avg : (p as any)[field];
            if (rawValue !== null && rawValue !== undefined) {
              if (!entry.values[field]) entry.values[field] = [];
              const num = typeof rawValue === 'number' ? rawValue : Number(rawValue);
              if (Number.isFinite(num)) {
                // Store the value but mark it as already aggregated
                entry.values[field].push(num);
              }
            }
          });
        });
      }

      // DEBUG: Log aggregation map before processing
      console.log(`  Aggregation map entries: ${aggregationMap.size}`);
      Array.from(aggregationMap.entries()).slice(0, 3).forEach(([key, value]) => {
        console.log(`    ${key}:`, {
          x: value.x,
          patternPointId: value.patternPointId,
          isParent: value.isParent,
          valueCount: Object.keys(value.values).map(field => `${field}=${value.values[field].length}`).join(', ')
        });
      });

      // Convert aggregated points to final format
      const processedPoints = Array.from(aggregationMap.values()).map((entry) => {
        const result: any = {
          x: entry.x,
          bucket: entry.x,
          patternPointId: entry.patternPointId,
          unit: entry.unit,
          isParent: entry.isParent,
        };

        // Aggregate collected values per field
        Object.keys(entry.values).forEach((field) => {
          const values = entry.values[field];
          if (values.length === 0) return;

          // For pattern points (non-parent), use the raw value directly
          // Backend already aggregated one value per pattern point per day
          if (!entry.isParent && values.length === 1) {
            result[field] = values[0];
            return;
          }

          // For parent aggregation, aggregate across all pattern points
          if (field === 'average' || field === 'median') {
            // Average of averages/medians
            result[field] = values.reduce((a, b) => a + b, 0) / values.length;
          } else if (field === 'min') {
            // Min of mins
            result[field] = Math.min(...values);
          } else if (field === 'max') {
            // Max of maxes
            result[field] = Math.max(...values);
          } else if (field === 'sum' || field === 'count') {
            // Sum of sums/counts
            result[field] = values.reduce((a, b) => a + b, 0);
          } else if (field === 'variance' || field === 'stddev') {
            // Average variance/stddev (simplified)
            result[field] = values.reduce((a, b) => a + b, 0) / values.length;
          }
        });

        return result;
      });

      // DEBUG: Log processed points before chart conversion
      console.log(`  Processed points: ${processedPoints.length}`);
      processedPoints.slice(0, 3).forEach((p, idx) => {
        console.log(`    Point ${idx}:`, {
          x: p.x,
          patternPointId: p.patternPointId,
          isParent: p.isParent,
          values: fieldsToUse.map(f => `${f}=${p[f]}`).join(', ')
        });
      });

      // Now process the aggregated points
      processedPoints.forEach((p) => {
        const rawX = p.x ?? p.bucket;
        const coerced = coerceXToScale(rawX, resolvedXScaleType);
        if (!coerced) return;

        const { x, sortKey } = coerced;

        fieldsToUse.forEach((field) => {
          const rawValue = field === 'average' ? p.average ?? p.avg : (p as any)[field];
          if (rawValue === null || rawValue === undefined) return;

          const value = typeof rawValue === 'number' ? rawValue : Number(rawValue);
          if (!Number.isFinite(value)) return;

          const fieldLabel = formatAggregationLabel(field);
          
          // Build series label based on parent/point mode
          let seriesLabel: string;
          if (p.isParent) {
            // Parent aggregated data
            seriesLabel = showMetricLabel ? `${metricLabel} - ${fieldLabel}` : fieldLabel;
          } else if (p.patternPointId !== null && p.patternPointId !== undefined) {
            // Pattern point specific data
            const point = patternPoints?.find((pp: any) => pp.id === p.patternPointId);
            const pointLabel = point?.label || point?.pointIndex || `Point ${p.patternPointId}`;
            if (showMetricLabel) {
              seriesLabel = `${metricLabel} - ${pointLabel} - ${fieldLabel}`;
            } else {
              seriesLabel = `${pointLabel} - ${fieldLabel}`;
            }
          } else {
            // Fallback
            seriesLabel = showMetricLabel ? `${metricLabel} - ${fieldLabel}` : fieldLabel;
          }

          // keep a stable key for sorting within a series
          points.push({
            x,
            value,
            series: seriesLabel,
            __seriesKey: seriesLabel,
            __sortKey: sortKey,
            __isParent: p.isParent,
          });
        });

        if (hasBandFields && showParent) {
          const minRaw = p.min;
          const maxRaw = p.max;
          if (minRaw === null || minRaw === undefined || maxRaw === null || maxRaw === undefined) return;

          const min = typeof minRaw === 'number' ? minRaw : Number(minRaw);
          const max = typeof maxRaw === 'number' ? maxRaw : Number(maxRaw);
          if (!Number.isFinite(min) || !Number.isFinite(max)) return;

          bands.push({
            x,
            min,
            max,
            bandSeries: metricLabel,
            __sortKey: sortKey,
          });
        }
      });
    });

    // sort points by series, then x
    const bySeries = new Map<string, PlotPoint[]>();
    points.forEach((p) => {
      if (!bySeries.has(p.__seriesKey)) bySeries.set(p.__seriesKey, []);
      bySeries.get(p.__seriesKey)!.push(p);
    });

    const orderedPoints = Array.from(bySeries.values()).flatMap((seriesPts) =>
      [...seriesPts].sort((a, b) => {
        const ak = a.__sortKey;
        const bk = b.__sortKey;
        if (typeof ak === 'number' && typeof bk === 'number') return ak - bk;
        return String(ak).localeCompare(String(bk));
      })
    );

    const orderedBands = [...bands].sort((a, b) => {
      const ak = a.__sortKey;
      const bk = b.__sortKey;
      if (typeof ak === 'number' && typeof bk === 'number') return ak - bk;
      return String(ak).localeCompare(String(bk));
    });

    const fallbackPoints: PlotPoint[] = [
      { x: new Date('2025-01-01'), value: 75, series: 'Average', __seriesKey: 'Average', __sortKey: Date.parse('2025-01-01') },
      { x: new Date('2025-01-02'), value: 78, series: 'Average', __seriesKey: 'Average', __sortKey: Date.parse('2025-01-02') },
      { x: new Date('2025-01-03'), value: 82, series: 'Average', __seriesKey: 'Average', __sortKey: Date.parse('2025-01-03') },
    ];

    const showPlaceholder = !!previewFallback && orderedPoints.length === 0;
    const displayPoints = showPlaceholder ? fallbackPoints : orderedPoints;
    const displayBands = displayPoints.length && orderedBands.length ? orderedBands : undefined;

    // DEBUG: Log final chart data
    console.log('\n✅ Final Chart Data:');
    console.log(`  Total chart points: ${displayPoints.length}`);
    console.log(`  Series breakdown:`);
    const seriesCounts = new Map<string, number>();
    displayPoints.forEach(p => {
      seriesCounts.set(p.series, (seriesCounts.get(p.series) || 0) + 1);
    });
    seriesCounts.forEach((count, series) => {
      console.log(`    ${series}: ${count} points`);
    });
    if (displayPoints.length > 0 && displayPoints.length <= 10) {
      console.log('  Sample data points:', displayPoints.slice(0, 5));
    }
    console.groupEnd();

    return {
      displayPoints,
      displayBands,
      unitLabel: resolvedUnitLabel,
      xScaleType: showPlaceholder ? 'time' : resolvedXScaleType, // fallback uses dates
      showPlaceholder,
    };
  }, [data, fields, metricLabels, patternPoints, showParent, showPoints, previewFallback, fallbackMetricKey]);

  const hasData = displayPoints.length > 0;
  const uniqueSeriesCount = useMemo(
    () => new Set(displayPoints.map((point) => point.series)).size,
    [displayPoints]
  );

  const formatNumericValue = (raw: unknown) => {
    const num = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(num)) return null;
    const formatted = num.toFixed(2);
    return unitLabel ? `${formatted} ${unitLabel}` : formatted;
  };

  const config: any = {
    data: displayPoints.map(({ __seriesKey, __sortKey, __isParent, ...rest }) => rest),
    xField: 'x',
    yField: 'value',
    seriesField: 'series',
    colorField: 'series',
    color: palette,
    legend: { color: { position: 'top', title: 'Series' } },

    scale: {
      x: { type: xScaleType },
      y: { type: 'linear' },
    },

    smooth: false,
    height: 236,
    lineStyle: {
      lineWidth: 2.5,
    },
    point: {
      size: 4,
      shape: 'circle',
      style: {
        lineWidth: 2,
        stroke: '#ffffff',
      },
    },
    tooltip: {
      shared: true,
      showMarkers: true,
      items: [
        (datum: any) => ({
          name: String(datum?.series || 'Value'),
          value: formatNumericValue(datum?.value) ?? '—',
          color: datum?.color,
        }),
      ],
    },
    axis: {
      y: {
        title: unitLabel || false,
        grid: true,
        gridStroke: 'rgba(226, 232, 240, 0.9)',
        gridLineWidth: 1,
      },
      x: {
        title: false,
        labelAutoRotate: false,
        labelAutoHide: true,
      },
    },
    animation: false,
  };

  return displayPoints.length > 0 ? (
    <div
      className="report-pattern-point-line-surface"
      style={
        {
          '--report-chart-accent': accentColor,
          '--report-chart-accent-soft': toRgba(accentColor, 0.1),
        } as React.CSSProperties
      }
    >
      {(title || uniqueSeriesCount > 0) ? (
        <div className="report-pattern-point-line-head">
          <div className="report-pattern-point-line-title">{title || 'Pattern trend'}</div>
          <div className="report-pattern-point-line-meta">
            {uniqueSeriesCount} series
          </div>
        </div>
      ) : null}
      {unitLabel ? (
        <div className="report-pattern-point-line-unit-row">
          <span className="report-pattern-point-line-unit">{unitLabel}</span>
        </div>
      ) : null}
      <div
        className="report-pattern-point-line-frame"
        style={{
          backgroundColor: toRgba(accentColor, 0.05),
          borderRadius: 10,
          padding: 8,
          border: `1px solid ${toRgba(accentColor, 0.12)}`,
        }}
      >
        <Line {...config} />
      </div>
    </div>
  ) : (
    <div className="report-pattern-point-line-empty">
      <Text type="secondary">No data</Text>
    </div>
  );
};

export default ReportPatternPointLineChart;
